/**
 * /feature-request-template 的中文版 — 对应 FeatureRequestTemplate.tsx。
 *
 * 差异化角度:一份功能请求模板(表单、工单、表格里的一行)只在录入那一刻
 * 抓住了诉求,之后就成了孤岛,和它本该推动的产品决策彻底脱节。本页先把真正
 * 的模板(八个字段,逐条写清)交到读者手上,让搜索的人拿到能直接用的东西,
 * 再说明 Draftlize 如何把每条功能请求连到相关的决策 / spec 卡片:一旦采纳就能
 * 一路追踪到落地,决策变了又能回溯到当初搭在它上面的那些请求。
 *
 * 中文内联(无 i18n),与 DecisionLogTemplateZh.tsx 及对比页保持一致。
 */
import { MarketingShell } from './MarketingShell';

export function FeatureRequestTemplateZh() {
  return (
    <MarketingShell
      currentLocale="zh"
      hreflangPath="/feature-request-template"
      documentTitle="功能请求模板(连着产品决策的那种)· Draftlize"
      documentDescription="一份可以直接抄走的功能请求模板 —— 标题、提出人与日期、问题或待办任务(JTBD)、建议方案、使用场景与频率、影响面与覆盖人数、优先级、关联的决策或 spec。问题在于:工单系统或表格里的功能请求表单是座孤岛,和它本该推动的产品决策彻底脱节。在 Draftlize,每条功能请求都连到相关的决策或 spec 卡片 —— 采纳后能追踪到落地,决策一变又能回溯。"
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">模板 · 功能请求</span>
          <h1 className="serif" style={{ fontSize: 'clamp(40px, 5vw, 64px)', lineHeight: 1, margin: '12px 0 24px' }}>
            一份功能请求模板,<br/>
            <em>而且真的有下文。</em>
          </h1>
          <p className="lede">
            你是冲着<em>功能请求模板</em>来的 —— 字段、标题、一份能丢进表单或文档里就用的东西。它就在下面,拿走。但要清楚它的失效方式:工单系统、表格、或一张录入工单里的功能请求表单,只在录入那一刻抓住了诉求,然后就把它晾在一边。这条请求从不和它本该推动的产品决策接上线,于是好点子死在 backlog 里,被采纳的那条也丢了「我们要做」到「实际做出了什么」之间的线索。Draftlize 给你同样的八个字段 —— 再把每条请求连到它牵涉的决策和 spec 卡片,让它从录入一路可追踪到落地。
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">模板本体</span>
            <h2 className="serif">八个字段。<em>抄到哪儿都能用。</em></h2>
          </div>
          <p>这就是模板的全部。放进表单、工单系统还是文档里都行 —— 它就是产品团队做需求录入一直在用的那个形状。直接照搬即可。下一节讲的是:当每条请求不再只是列表里的一行、而是开始连向它所推动的决策,会发生什么变化。</p>
        </div>

        <div
          style={{
            border: '1px solid var(--ink-0)',
            background: 'var(--paper-0)',
            fontFamily: "'Inter', sans-serif",
          }}
        >
          {[
            {
              k: '标题',
              hint: '必填',
              body: '一句话说清诉求,而且要落在结果上 ——「让用户把一个对话导出为 PDF」,而不是「PDF 功能」。如果一句话说不清你想达成的结果,这条请求还没成形。',
            },
            {
              k: '提出人与日期',
              hint: '谁 / 何时',
              body: '谁提的、什么时候提的。不是为了记功,是为了回头追问。这条请求在评审时再冒出来,这里就是你回去补表单没抓住的那点细节的人。',
            },
            {
              k: '问题或待办任务(JTBD)',
              hint: '为什么',
              body: '底层的真实需求,不要把方案塞进去。用户到底想完成什么、今天卡在哪。这是哪怕建议方案被推翻、也依然站得住的那个字段。',
            },
            {
              k: '建议方案',
              hint: '怎么做',
              body: '提出人设想的解法 —— 是起点,不是承诺。是有用的信号,但要和「问题」分开放,这样你才能采纳需求、同时否掉那个具体做法。',
            },
            {
              k: '使用场景与频率',
              hint: '什么时候疼',
              body: '它出现的具体情境、以及多频繁。「每周一拼周报的时候」胜过「有了会挺好」。频率,正是区分「天天硌人的刺」和「一次性需求」的那条线。',
            },
            {
              k: '影响面与覆盖人数',
              hint: '有多大',
              body: '它影响谁、影响多深 —— 牵动多少用户、痛到什么程度、解锁了什么。这个字段让你能把两条单看都「挺合理」的请求拿来比较。',
            },
            {
              k: '优先级',
              hint: '排序',
              body: '它在所有事项里排在哪 —— 现在做、接着做、以后做、还是不做。优先级是一种比较,不是一种感觉;它只有相对于其它待处理请求才有意义。',
            },
            {
              k: '关联的决策或 spec',
              hint: '它牵涉什么',
              body: '这条请求推动、或依赖的那个产品决策或 spec。在表单里这是一个你会留空的空格。它正是决定这条请求录入之后到底有没有下文的那个字段。',
            },
          ].map((f, i) => (
            <div
              key={f.k}
              style={{
                display: 'grid',
                gridTemplateColumns: '220px 1fr',
                gap: 24,
                padding: '20px 22px',
                borderTop: i === 0 ? 0 : '1px dashed var(--rule)',
                alignItems: 'baseline',
              }}
            >
              <div>
                <span
                  className="serif"
                  style={{ fontSize: 19, fontWeight: 500, color: 'var(--ink-0)', display: 'block', lineHeight: 1.2 }}
                >
                  {f.k}
                </span>
                <span
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 9.5,
                    letterSpacing: '.1em',
                    textTransform: 'uppercase',
                    color: 'var(--amber)',
                    fontWeight: 700,
                  }}
                >
                  {f.hint}
                </span>
              </div>
              <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.6, color: 'var(--ink-1)' }}>{f.body}</p>
            </div>
          ))}
        </div>

        <p
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 11,
            letterSpacing: '.06em',
            color: 'var(--ink-3)',
            textAlign: 'center',
            marginTop: 18,
          }}
        >
          这就是模板。往下:为什么一张请求表单是座孤岛 —— 以及我们如何把每条请求接进它本该推动的决策里。
        </p>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">为什么请求表单<em>是座孤岛。</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">录入抓住了诉求,然后把它晾在那儿</h3>
            <p>功能请求表单擅长一件事:把诉求从某人脑子里掏出来、装进字段。但表单是条死路。它躺在工单系统、表格、或一份 typeform 导出里,没有任何线索把它接到它本该推动的产品决策上。被「录入」,和被「认真对待」,是两回事。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">采纳了的请求,丢了通往落地的线索</h3>
            <p>你评审了一条请求,说「做」。然后呢?「要做它」这个决策在一处,spec 在另一处,最初那条请求在第三处。等真正上线了,没人答得出「我们到底解决了当初问的那个问题吗」—— 因为请求和实际的活儿从没被连起来。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">决策一变,这些请求无从回溯</h3>
            <p>你在第七周推翻了一个产品决策。哪些功能请求是搭在它上面的?那些因为旧决策才被你采纳的、那些因为它才被你否掉的 —— 全都悄悄成了孤儿。没有任何线索把它们连回那个决策,于是这场推翻是闭着眼睛做的。</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">同一份模板,<em>接进了网里。</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">每条请求是张卡片,不是一行</h3>
            <p>在 Draftlize,一条功能请求不是表格里的一行 —— 而是一张带着那几个字段的结构化卡片:标题、问题、建议方案、使用场景、影响面、优先级,还有那条链接。结构化、有 ID 可寻址、能被某个决策或 spec 引用,而不是搁浅在录入工具里。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">一旦采纳,就能追踪到落地</h3>
            <p>因为请求连着它所推动的决策和 spec,采纳它就不再是真空里改个状态 —— 而是图里多了一条边。你能从一个已上线的功能,回溯到当初提出它的那条请求,用一条链接、而不是凭记忆,答出「我们解决的是不是那个真实的待办」。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">决策一变,关联的请求自动标 stale</h3>
            <p>因为这条链接是真实的、不是一句备注,推翻一个决策会让搭在它上面的每条请求自动变为 stale —— 就像构建系统让被改文件下游的一切都失效那样。而且 AI agent 会在起草下一份 spec 前,通过 MCP 读到这些请求 —— 于是录入成了上下文,而不是一份存档。</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          一张请求表单告诉你某人想要什么。它没法告诉你:那个能成全它的决策,如今还成不成立。
        </blockquote>
        <cite>留下这八个字段。让底座替你把它们连在一起。</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">常见问题</span>
            <h2 className="serif">FAQ。</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820, fontFamily: "'Inter', sans-serif" }}>
          {[
            { q: '什么是功能请求？', a: '功能请求是把用户或干系人希望产品做的事结构化记下来——背后的问题、拟采取的方案、使用场景、影响。好的请求抓的是底层那个「要办成的事」，而不只是它要的那个功能，于是你解决的是真实需求，而不是字面请求。' },
            { q: '功能请求模板应该包含什么？', a: '标题、问题、拟采取的方案、使用场景、影响、优先级，以及一条连到它所影响决策或 spec 的链接。最要紧的是「问题」字段：一个只写方案（「加个下拉框」）的请求，藏住了它想办成的事，而那件事才是你真正拿来排优先级的。' },
            { q: '怎么给功能请求排优先级？', a: '用一致的框架打分——RICE、价值对成本、或加权打分——依据的是问题和影响，而不是请求的数量。十个人请求同一个绕行方案，是一个问题，不是十个功能。按「要办成的事」排序，再决定。' },
            { q: 'Draftlize 怎么让功能请求不被孤立？', a: '每条请求都是一张连着它所影响决策与 spec 的卡片，于是「接受」不是真空里的状态变更，而是图里的一条边。日后翻掉一个决策，凡是建立在它之上的请求都自动标成 stale——翻案不再是盲翻。' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.7, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">开始收集请求,<em>注册送 $5。</em></h2>
        </div>
        <div className="trust-strip">
          <span>新账号送 $5</span>
          <span>用多少付多少</span>
          <span>余额永不过期</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            照搬上面的模板,或者开个项目、让 agent 把每条请求记成一张卡片 —— 再把每一条都连到它牵涉的决策和 spec,然后替你把整条链路保持最新。
          </p>
          <a className="btn-primary" href="/">注册送 $5,免费开始</a>
        </div>
      </section>
    </MarketingShell>
  );
}
